import React, { useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioButton,
} from '../../components/BrioComponents';

// Create or edit a zone. Pass `zone` to edit; `parent` to create a child.
function CreateZoneModal({ zone, parent, onClose, onSubmit }) {
  const isEdit = !!zone;
  const [form, setForm] = useState({
    code: zone?.code || '',
    label: zone?.label || '',
    zoneType: zone?.zoneType || '',
    builtUpArea: zone?.builtUpArea ?? '',
    carpetArea: zone?.carpetArea ?? '',
    constructionArea: zone?.constructionArea ?? '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.code.trim()) { setError('Zone code is required.'); return; }
    if (!form.label.trim()) { setError('Zone label is required.'); return; }
    setSubmitting(true);
    const result = await onSubmit({ ...form, parentId: parent?.id || null });
    setSubmitting(false);
    if (result.success) onClose();
    else setError(result.error || 'Failed to save zone.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Saving…' : (isEdit ? 'Save zone' : 'Create zone')}
      </BrioButton>
    </>
  );

  const title = isEdit
    ? `Edit zone ${zone.code}`
    : `New zone${parent ? ` under ${parent.code}` : ' (top level)'}`;

  return (
    <BrioModal title={title} onClose={onClose} width="sm" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <BrioModalField label="Code *" hint="Short identifier, unique within its parent. e.g. villa-1, gf, br-2.">
            <BrioInput value={form.code} onChange={update('code')} placeholder="villa-1" autoFocus />
          </BrioModalField>
          <BrioModalField label="Label *">
            <BrioInput value={form.label} onChange={update('label')} placeholder="Villa 1" />
          </BrioModalField>
          <BrioModalField label="Zone type" hint="Optional. e.g. villa, floor, room, public-area.">
            <BrioInput value={form.zoneType} onChange={update('zoneType')} placeholder="villa" />
          </BrioModalField>
          <BrioModalField label="Built-up area (m²)" hint="Optional — used for cost / m² of this zone.">
            <BrioInput type="number" step="any" value={form.builtUpArea} onChange={update('builtUpArea')} placeholder="e.g. 210" />
          </BrioModalField>
          <BrioModalField label="Carpet area (m²)">
            <BrioInput type="number" step="any" value={form.carpetArea} onChange={update('carpetArea')} placeholder="e.g. 150" />
          </BrioModalField>
          <BrioModalField label="Construction area (m²)">
            <BrioInput type="number" step="any" value={form.constructionArea} onChange={update('constructionArea')} placeholder="e.g. 235" />
          </BrioModalField>
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default CreateZoneModal;
